import axios from "axios";
import { addNotification } from "../redux/slices/notification-slice";
import { setUserInfo } from "../redux/slices/user-slice";
import { store } from "../redux/store";
import {
	checkLocalStorageTokens,
	getRefreshTokenFromStorage,
	removeTokens,
	setTokensOnLocalStorage,
	TokenUserDTO,
} from "../utils/AuthFunctions";

const baseURL = import.meta.env.VITE_BASE_URL;
const instance = axios.create({
	baseURL: baseURL,
	responseType: "json",
	headers: {
		"Content-Type": "application/json",
	},
});

instance.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		if (error.response?.data?.code === "216" && checkLocalStorageTokens()) {
			api.removeAuthHeader();
			api.post("/api/refresh-token", {
				refreshToken: getRefreshTokenFromStorage(),
			})
				.then((response: TokenUserDTO) => {
					setTokensOnLocalStorage(
						response.accessToken,
						response.refreshToken
					);
					api.setAuthHeader(response.accessToken);
					store.dispatch(
						setUserInfo({ email: response.email, name: response.name })
					);
					window.location.reload();
				})
				.catch(() => {
					removeTokens();
					api.removeAuthHeader();
					window.location.reload();
				});
		} else if (
			!axios.isCancel(error) &&
			!["206", "232"].includes(error.response?.data?.code)
		) {
			store.dispatch(
				addNotification({
					message: error?.response?.data?.message,
					type: "danger",
				})
			);
		}
		return Promise.reject(error);
	}
);

let reqControl = axios.CancelToken.source();

const api = {
	get<T = any>(url: string, params?: any) {
		return instance
			.get<T>(url, { ...params, cancelToken: reqControl.token })
			.then((response) => {
				return response.data;
			});
	},

	post<T = any>(url: string, data: any) {
		return instance.post<T>(url, data).then((response) => {
			return response.data;
		});
	},

	put<T = any>(
		url: string,
		data: any,
		headers = { "content-type": "application/json" },
		fullResponse = false
	) {
		return instance.put<T>(url, data, { headers }).then((response) => {
			return fullResponse ? response : response.data;
		});
	},

	delete(url: string) {
		return instance.delete(url).then(() => {
			return true;
		});
	},

	setAuthHeader(accessToken: string | null) {
		if (accessToken && accessToken !== "undefined") {
			instance.defaults.headers.common[
				"Authorization"
			] = `Bearer ${accessToken}`;
		}
	},

	removeAuthHeader() {
		delete instance.defaults.headers.common["Authorization"];
	},

	cancelAllGetRequest() {
		reqControl?.cancel();
		reqControl = axios.CancelToken.source();
	},
};

export default api;
